import type { NavigationItem } from '@/types/navigation'
import { sortRootSections, sortSiblings } from '@/utils/navigationSort'
import { navigationFolderKey } from '@/utils/navigationFolderKey'
import { hasNavigationItemPermission } from '@/utils/navigationPermissionAccess'

const PARENT_ROUTES_WITH_CHILDREN = [
  '/reporting-dashboards',
  '/crm',
  '/product-dev/dashboard',
  '/product-dev/pim',
  '/product-dev/lists/universal-intake',
  '/surveys',
  '/inquiries',
  '/machines',
  '/sales-history',
]

export interface NavigationFilterContext {
  hasPermission: (permission: string) => boolean
  ddPermit: 'supervisor' | 'user'
}

export function sortNavigationChildren(items: NavigationItem[], isRoot: boolean): NavigationItem[] {
  return isRoot ? sortRootSections(items) : sortSiblings(items)
}

export function isActiveNavigationRoute(currentPath: string, routePath: string | null | undefined): boolean {
  if (!routePath) return false
  if (currentPath === routePath) return true

  if (routePath === '/order-entry/queue') {
    return currentPath.startsWith('/order-entry/orders/')
  }

  if (PARENT_ROUTES_WITH_CHILDREN.includes(routePath)) {
    return currentPath === routePath
  }

  return currentPath.startsWith(`${routePath}/`)
}

export function isRouteInNavigationSubtree(item: NavigationItem, path: string): boolean {
  if (item.route_path && isActiveNavigationRoute(path, item.route_path)) {
    return true
  }

  return (item.children ?? []).some((child) => isRouteInNavigationSubtree(child, path))
}

export function findFirstLinkRoute(item: NavigationItem): string | null {
  if (item.item_type === 'link' && item.route_path) {
    return item.route_path
  }

  for (const child of sortSiblings(item.children ?? [])) {
    const found = findFirstLinkRoute(child)
    if (found) return found
  }

  return null
}

export function isExcludedFromMainSidebar(item: NavigationItem, isRoot: boolean): boolean {
  if (!isRoot) {
    return false
  }

  if (item.key === 'org-chart') {
    return true
  }

  return item.metadata?.sidebar_placement === 'footer'
}

export function filterNavigationTree(
  items: NavigationItem[],
  ctx: NavigationFilterContext,
  isRoot = true,
): NavigationItem[] {
  const sorted = sortNavigationChildren(items, isRoot)
  const result: NavigationItem[] = []

  for (const item of sorted) {
    if (!item.is_visible) continue
    if (isExcludedFromMainSidebar(item, isRoot)) continue
    if (item.metadata?.dd_supervisor_only === true && ctx.ddPermit !== 'supervisor') continue

    const isContainer = item.item_type === 'section' || item.item_type === 'group'
    const showIfAny = item.metadata?.show_if_any_permission
    if (
      Array.isArray(showIfAny) &&
      showIfAny.length > 0 &&
      !showIfAny.some((permission) => typeof permission === 'string' && ctx.hasPermission(permission))
    ) {
      continue
    }

    if (!isContainer && !hasNavigationItemPermission(ctx.hasPermission, item.permission)) continue

    if (item.item_type === 'link' || item.item_type === 'external') {
      result.push(item)
      continue
    }

    const filteredChildren = item.children?.length
      ? filterNavigationTree(item.children, ctx, false)
      : []

    if (filteredChildren.length > 0) {
      result.push({ ...item, children: filteredChildren })
    }
  }

  return result
}

export function collectOpenFolderKeysForRoute(
  items: NavigationItem[],
  path: string,
  ancestorKeys: string[] = [],
): string[] {
  const keys = new Set<string>()

  for (const item of items) {
    if (item.item_type === 'section' || item.item_type === 'group') {
      const nextAncestors = [...ancestorKeys, item.key]
      const childKeys = collectOpenFolderKeysForRoute(item.children ?? [], path, nextAncestors)

      if (childKeys.length > 0 || isRouteInNavigationSubtree(item, path)) {
        for (const ancestorKey of nextAncestors) {
          keys.add(navigationFolderKey(ancestorKey))
        }
        for (const childKey of childKeys) {
          keys.add(childKey)
        }
      }
      continue
    }

    if (item.route_path && isActiveNavigationRoute(path, item.route_path)) {
      for (const ancestorKey of ancestorKeys) {
        keys.add(navigationFolderKey(ancestorKey))
      }
    }
  }

  return [...keys]
}
